import * as t from 'io-ts';

import {makeApiSerializableType, makeApiType, makeEnumRuntimeType, RTagIdRef} from './apiTypes';
import {
  HighlightTypesEnum,
  parseHighlight,
  RHighlightColorBase,
  RHighlightEmojiBase,
  RHighlightNoneBase,
} from './highlightBaseModel';

/*
 * Runtime type.
 */

export enum TagTypesEnum {
  INBOX = 'main_inbox',
  STARRED = 'starred',
  TAG = 'tag',
  VIEW = 'view',
  SLA_RULE = 'sla_rule',
}

export enum TagExtrasEnum {
  SYSTEM_TAG = 'is_system_tag',
}

export enum TagBehaviorsEnum {
  /** Tag available for analytics */
  CAN_ANALYTICS = 'can_analytics',
  /** Tag available in dropdown to apply on conversation */
  CAN_APPLY_FROM_LIST = 'can_apply_from_list',
  /** Tag can be displayed at top of P3 or in P2 */
  CAN_DISPLAY = 'can_display',
  /** Tag can be used to drop conversations (remove from inbox) */
  CAN_DROP = 'can_drop',
  /** Tag can be listed when selecting a parent tag */
  CAN_PARENT = 'can_parent',
  /** Tag can be pinned in P1 */
  CAN_PIN = 'can_pin',
  /** Tag available in rules */
  CAN_RULE = 'can_rule',
  /** Tag can be searched for (suggestions + search bar) */
  CAN_SUGGEST = 'can_suggest',
  /** Tag can be edited/deleted (settings) */
  CAN_UPDATE = 'can_update',
  /** Tag can be a custom view (the ones defined in settings) */
  CAN_CUSTOM_VIEW = 'can_custom_view',
}

const RTagBehaviorsEnum = makeEnumRuntimeType(TagBehaviorsEnum);

// Common schema.
const RBaseTag = t.type({
  id: t.Integer,
  url: t.string,
  namespace: t.string,
  alias: t.string,
  name: t.string,
  parentTagId: t.union([t.undefined, RTagIdRef]),
  childTagIds: t.readonlyArray(t.Integer),
  archivedChildTagIds: t.union([t.undefined, t.readonlyArray(t.Integer)]),
  activeChildTagIds: t.union([t.undefined, t.readonlyArray(t.Integer)]),
  previousParentTagId: t.union([t.undefined, RTagIdRef]),
  isMainInboxTag: t.boolean,
  isView: t.boolean,
  isSystemTag: t.boolean,
  showInCells: t.boolean,
  isVisibleInAllInboxes: t.boolean,
  inboxIds: t.readonlyArray(t.Integer),
  updatedAt: t.Integer,
  createdAt: t.Integer,
  behaviors: t.readonlyArray(RTagBehaviorsEnum),
  archivedAt: t.union([t.undefined, t.Integer]),
  lastUsedAt: t.union([t.undefined, t.Integer]),
});

const RBaseTagHighlightNone = t.intersection([RBaseTag, RHighlightNoneBase]);
const RBaseTagHighlightEmoji = t.intersection([RBaseTag, RHighlightEmojiBase]);
const RBaseTagHighlightColor = t.intersection([RBaseTag, RHighlightColorBase]);

const RTagHighlightNone = t.readonly(RBaseTagHighlightNone);
const RTagHighlightEmoji = t.readonly(RBaseTagHighlightEmoji);
const RTagHighlightColor = t.readonly(RBaseTagHighlightColor);

const RTagDirty = t.readonly(
  t.taggedUnion('type', [RBaseTagHighlightNone, RBaseTagHighlightEmoji, RBaseTagHighlightColor]),
  'tag',
);

/*
 * Type.
 */

export interface TagHighlightNoneModel extends t.TypeOf<typeof RTagHighlightNone> {}
export interface TagHighlightEmojiModel extends t.TypeOf<typeof RTagHighlightEmoji> {}
export interface TagHighlightColorModel extends t.TypeOf<typeof RTagHighlightColor> {}
export type TagModel = TagHighlightNoneModel | TagHighlightEmojiModel | TagHighlightColorModel;

export const RTag = t.clean<TagModel>(RTagDirty as any);
RTagIdRef.setTargetType(RTag);
const RTags = t.readonlyArray(RTag);
const RTagPath = t.readonlyArray(RTag);
const RTagsPath = t.readonlyArray(RTagPath);

// TagSearch like model.
export interface TagPathModel extends t.TypeOf<typeof RTagPath> {}
export interface TagsPathModel extends t.TypeOf<typeof RTagsPath> {}

/*
 * IO methods.
 */

export function parseTag(src: any): TagModel {
  const extra = Array.isArray(src.extra) && src.extra;
  return {
    id: src.id,
    url: src.url,
    namespace: src.namespace,
    alias: src.alias,
    name: src.name,
    isVisibleInAllInboxes: src.all_inboxes,
    inboxIds: src.inbox_ids,
    childTagIds: src.child_tag_ids,
    archivedChildTagIds: src.archived_child_tag_ids,
    activeChildTagIds: src.active_child_tag_ids,
    parentTagId: (src.parent_tag && src.parent_tag.id) || undefined,
    /** previousParentTagId does not come from the backend, it is assigned during optimistic updates in saveTagActions.ts.  */
    previousParentTagId: undefined,
    isMainInboxTag: src.type === TagTypesEnum.INBOX,
    isView: src.type === TagTypesEnum.VIEW,
    showInCells: src.is_visible_in_p2,
    isSystemTag: extra && extra.includes(TagExtrasEnum.SYSTEM_TAG),
    updatedAt: src.updated_at,
    createdAt: src.created_at,
    behaviors: (Array.isArray(src.behaviors) && src.behaviors.filter(RTagBehaviorsEnum.is)) || [],
    archivedAt: src.archived_at || undefined,
    lastUsedAt: src.last_used_at || undefined,
    ...parseHighlight(src.highlight),
  };
}

export function parseTags(src: any) {
  return src.tags && src.tags.map(parseTag);
}

function parseTagPath(src: any): TagPathModel {
  let currentTag = src;
  let tagPath: TagPathModel = [];

  while (currentTag?.id) {
    tagPath = [...tagPath, parseTag(currentTag)];
    currentTag = currentTag.parent_tag;
  }

  return tagPath;
}

function parseTagsPath(src: any): TagsPathModel {
  return src.tags && src.tags.map(parseTagPath);
}

export function serializeTag(tag: TagModel): any {
  return {
    name: tag.name,
    highlight: serializeHighlight(tag),
    parent_tag_id: tag.parentTagId || null,
    is_visible_in_p2: tag.showInCells,
    all_inboxes: tag.isVisibleInAllInboxes,
    inbox_ids: tag.inboxIds,
  };
}

function serializeHighlight(tag: TagModel) {
  switch (tag.type) {
    case HighlightTypesEnum.EMOJI:
      return tag.highlightEmoji;

    case HighlightTypesEnum.COLOR:
      return tag.highlightColor.toString();

    default:
      return null;
  }
}

/*
 * API type.
 */

export const ApiTag = makeApiSerializableType('tag', RTag, parseTag, serializeTag);
export const ApiSingleTag = makeApiType('tag', RTag, parseTag);
export const ApiTags = makeApiType('tags', RTags, parseTags);
export const ApiTagsPath = makeApiType('tags', RTagsPath, parseTagsPath);
